// src/components/common/DataTable/DataTable.tsx

import {useMemo, useState, useCallback, Fragment} from "react";
import {ChevronUp, ChevronDown, ChevronRight} from "lucide-react";
import {clsx} from "clsx";
import {RowActions, BulkActions, TableHeaderActions} from "./TableActions";
import TableFilters from "./TableFilters";
import TablePagination from "./TablePagination";
import {Badge} from "../../ui";
import {EmptyState, LoadingSpinner} from "../";
import type {
  DataTableColumn,
  DataTableProps,
  DataTableSort,
} from "./DataTableTypes";

const DataTable = <T extends Record<string, unknown>>({
  // Data
  data,
  columns,
  loading = false,

  // Identification
  rowKey,

  // Actions
  actions = [],
  bulkActions = [],

  // Selection
  selection,

  // Pagination
  pagination,

  // Sorting
  sortable = true,
  defaultSort,
  onSort,

  // Filtering
  filters = [],
  onFilter,

  // Search
  searchable = true,
  searchPlaceholder = "Search...",
  onSearch,

  // Layout & Styling
  size = "md",
  bordered = false,
  striped = true,
  hoverable = true,
  className = "",
  tableClassName = "",

  // Empty State
  emptyText = "No data available",
  emptyIcon,

  // Expandable
  expandable,

  // Scroll
  scroll,

  // Custom Renderers
  headerRender,
  footerRender,

  // Events
  onRow,

  // Responsive
  responsive = true,
}: DataTableProps<T>) => {
  const [sortState, setSortState] = useState<DataTableSort | null>(
    defaultSort || null
  );
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>(
    selection?.selectedRowKeys || []
  );
  const [expandedRowKeys, setExpandedRowKeys] = useState<React.Key[]>(
    expandable?.defaultExpandedRowKeys || []
  );

  // Get row key function
  const getRowKey = useCallback(
    (record: T, index: number): React.Key => {
      if (typeof rowKey === "function") {
        return rowKey(record);
      }
      return record[rowKey] as React.Key;
    },
    [rowKey]
  );

  // Handle sorting
  const handleSort = useCallback(
    (field: string) => {
      if (!sortable) return;

      let newSort: DataTableSort | null = null;

      if (sortState?.field === field) {
        // Toggle direction or clear sort
        if (sortState.order === "asc") {
          newSort = {field, order: "desc"};
        } else {
          newSort = null; // Clear sort
        }
      } else {
        // New field
        newSort = {field, order: "asc"};
      }

      setSortState(newSort);
      onSort?.(newSort);
    },
    [sortable, sortState, onSort]
  );

  // Handle selection
  const handleSelection = useCallback(
    (keys: React.Key[], records: T[]) => {
      setSelectedRowKeys(keys);
      selection?.onChange?.(keys, records);
    },
    [selection]
  );

  const handleSelectAll = useCallback(
    (checked: boolean) => {
      if (checked) {
        const allKeys = data.map((record, index) => getRowKey(record, index));
        handleSelection(allKeys, data);
      } else {
        handleSelection([], []);
      }
    },
    [data, getRowKey, handleSelection]
  );

  const handleSelectRow = useCallback(
    (record: T, index: number, checked: boolean) => {
      const key = getRowKey(record, index);
      let newKeys: React.Key[];
      let newRecords: T[];

      if (checked) {
        newKeys = [...selectedRowKeys, key];
        newRecords = [
          ...(selectedRowKeys
            .map((k) => data.find((r, i) => getRowKey(r, i) === k))
            .filter(Boolean) as T[]),
          record,
        ];
      } else {
        newKeys = selectedRowKeys.filter((k) => k !== key);
        newRecords = newKeys
          .map((k) => data.find((r, i) => getRowKey(r, i) === k))
          .filter(Boolean) as T[];
      }

      handleSelection(newKeys, newRecords);
    },
    [selectedRowKeys, data, getRowKey, handleSelection]
  );

  // Handle expansion
  const handleExpand = useCallback(
    (expanded: boolean, record: T, index: number) => {
      const key = getRowKey(record, index);
      let newExpandedKeys: React.Key[];

      if (expanded) {
        newExpandedKeys = [...expandedRowKeys, key];
      } else {
        newExpandedKeys = expandedRowKeys.filter((k) => k !== key);
      }

      setExpandedRowKeys(newExpandedKeys);
      expandable?.onExpand?.(expanded, record);
      expandable?.onExpandedRowsChange?.(newExpandedKeys);
    },
    [expandedRowKeys, expandable, getRowKey]
  );

  // Selection state
  const selectedRecords = useMemo(() => {
    return selectedRowKeys
      .map((key) =>
        data.find((record, index) => getRowKey(record, index) === key)
      )
      .filter(Boolean) as T[];
  }, [selectedRowKeys, data, getRowKey]);

  const isAllSelected =
    data.length > 0 && selectedRowKeys.length === data.length;
  const isIndeterminate =
    selectedRowKeys.length > 0 && selectedRowKeys.length < data.length;

  // Table size classes
  const sizeClasses = {
    sm: "text-xs",
    md: "text-sm",
    lg: "text-base",
  };

  const paddingClasses = {
    sm: "px-3 py-2",
    md: "px-4 py-3",
    lg: "px-6 py-4",
  };

  // Render sort icon
  const renderSortIcon = (column: DataTableColumn<T>) => {
    if (!column.sortable || !sortable) return null;

    const field = column.key as string;
    const isActive = sortState?.field === field;

    return (
      <span className="ml-2 flex flex-col">
        <ChevronUp
          className={clsx(
            "w-3 h-3 -mb-1",
            isActive && sortState?.order === "asc"
              ? "text-primary-600"
              : "text-gray-400"
          )}
        />
        <ChevronDown
          className={clsx(
            "w-3 h-3",
            isActive && sortState?.order === "desc"
              ? "text-primary-600"
              : "text-gray-400"
          )}
        />
      </span>
    );
  };

  // Render cell content
  const renderCell = (column: DataTableColumn<T>, record: T, index: number) => {
    const value = record[column.key as keyof T];

    if (column.render) {
      return column.render(value, record, index);
    }

    // Handle null/undefined values
    if (value === null || value === undefined) {
      return <span className="text-gray-400 dark:text-gray-500">-</span>;
    }

    // Handle boolean values
    if (typeof value === "boolean") {
      return (
        <Badge variant={value ? "success" : "danger"}>
          {value ? "Yes" : "No"}
        </Badge>
      );
    }

    // Handle arrays
    if (Array.isArray(value)) {
      return <span>{value.length} items</span>;
    }

    // Handle objects
    if (typeof value === "object") {
      return <span className="text-gray-400 dark:text-gray-500">[Object]</span>;
    }

    // Default string representation
    return <span>{String(value)}</span>;
  };

  // Main table content
  const tableContent = (
    <div
      className={clsx(
        // "overflow-hidden",
        bordered && "border border-gray-200 dark:border-dark-700 rounded-lg",
        className
      )}
    >
      {/* Filters */}
      {(searchable || filters.length > 0) && (
        <TableFilters
          searchable={searchable}
          searchPlaceholder={searchPlaceholder}
          filters={filters}
          onSearch={onSearch ? (value) => onSearch(value) : () => {}}
          onFilter={onFilter ? (filters) => onFilter(filters) : () => {}}
        />
      )}

      {/* Bulk Actions */}
      {bulkActions.length > 0 && selectedRowKeys.length > 0 && (
        <BulkActions
          selectedRecords={selectedRecords}
          selectedKeys={selectedRowKeys}
          actions={bulkActions}
          onClearSelection={() => handleSelection([], [])}
        />
      )}

      {/* Table */}
      <div
        className={clsx(
          scroll?.x && "overflow-x-auto",
          scroll?.y && "overflow-y-auto"
        )}
        style={{maxHeight: scroll?.y}}
      >
        <table
          className={clsx(
            "w-full  divide-y divide-gray-200 dark:divide-dark-700",
            sizeClasses[size],
            tableClassName
          )}
        >
          {/* Header */}
          <thead className="bg-gray-50 dark:bg-dark-800">
            <tr>
              {/* Selection column */}
              {selection && (
                <th className={clsx("relative", paddingClasses[size])}>
                  {selection.type !== "radio" && (
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        checked={isAllSelected}
                        ref={(input) => {
                          if (input) input.indeterminate = isIndeterminate;
                        }}
                        onChange={(e) => handleSelectAll(e.target.checked)}
                        className="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                      />
                    </div>
                  )}
                </th>
              )}

              {/* Expandable column */}
              {expandable && <th className={paddingClasses[size]} />}

              {/* Data columns */}
              {columns.map((column, index) => (
                <th
                  key={index}
                  className={clsx(
                    paddingClasses[size],
                    "text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider",
                    column.sortable &&
                      sortable &&
                      "cursor-pointer hover:bg-gray-100 dark:hover:bg-dark-700",
                    column.headerClassName
                  )}
                  style={{width: column.width}}
                  onClick={() =>
                    column.sortable && handleSort(column.key as string)
                  }
                >
                  {headerRender ? (
                    headerRender(column, index)
                  ) : (
                    <div className="flex items-center">
                      {column.title}
                      {renderSortIcon(column)}
                    </div>
                  )}
                </th>
              ))}

              {/* Actions column */}
              {actions.length > 0 && (
                <th className={clsx(paddingClasses[size], "text-left")}>
                  Actions
                </th>
              )}
            </tr>
          </thead>

          {/* Body */}
          <tbody className="bg-white dark:bg-dark-800 divide-y divide-gray-200 dark:divide-dark-700">
            {loading ? (
              <tr>
                <td
                  colSpan={
                    columns.length +
                    (selection ? 1 : 0) +
                    (expandable ? 1 : 0) +
                    (actions.length > 0 ? 1 : 0)
                  }
                  className="text-center py-12"
                >
                  <LoadingSpinner size="lg" />
                </td>
              </tr>
            ) : data.length === 0 ? (
              <tr>
                <td
                  colSpan={
                    columns.length +
                    (selection ? 1 : 0) +
                    (expandable ? 1 : 0) +
                    (actions.length > 0 ? 1 : 0)
                  }
                  className="text-center py-12"
                >
                  <EmptyState
                    icon={emptyIcon}
                    title={
                      typeof emptyText === "string"
                        ? emptyText
                        : "No data available"
                    }
                  />
                </td>
              </tr>
            ) : (
              data.map((record, index) => {
                const key = getRowKey(record, index);
                const isSelected = selectedRowKeys.includes(key);
                const isExpanded = expandedRowKeys.includes(key);
                const rowProps = onRow?.(record, index) || {};

                return (
                  <Fragment key={key}>
                    <tr
                      className={clsx(
                        striped &&
                          index % 2 === 1 &&
                          "bg-gray-50 dark:bg-dark-700/50",
                        hoverable && "hover:bg-gray-50 dark:hover:bg-dark-700",
                        isSelected && "bg-primary-50 dark:bg-primary-900/20",
                        "transition-colors"
                      )}
                      {...rowProps}
                    >
                      {/* Selection cell */}
                      {selection && (
                        <td className={paddingClasses[size]}>
                          <div className="flex items-center">
                            <input
                              type={selection.type || "checkbox"}
                              name={
                                selection.type === "radio"
                                  ? "table-selection"
                                  : undefined
                              }
                              checked={isSelected}
                              onChange={(e) =>
                                handleSelectRow(record, index, e.target.checked)
                              }
                              {...(selection.getCheckboxProps?.(record) || {})}
                              className="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                            />
                          </div>
                        </td>
                      )}

                      {/* Expandable cell */}
                      {expandable && (
                        <td className={paddingClasses[size]}>
                          {expandable.rowExpandable?.(record) !== false && (
                            <button
                              onClick={() =>
                                handleExpand(!isExpanded, record, index)
                              }
                              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                            >
                              {isExpanded ? (
                                <ChevronDown className="w-4 h-4" />
                              ) : (
                                <ChevronRight className="w-4 h-4" />
                              )}
                            </button>
                          )}
                        </td>
                      )}

                      {/* Data cells */}
                      {columns.map((column, columnIndex) => (
                        <td
                          key={columnIndex}
                          className={clsx(
                            paddingClasses[size],
                            column.align === "center" && "text-center",
                            column.align === "right" && "text-right",
                            column.className,
                            column.ellipsis && "max-w-0 truncate"
                          )}
                          style={{width: column.width}}
                        >
                          {renderCell(column, record, index)}
                        </td>
                      ))}

                      {/* Actions cell */}
                      {actions.length > 0 && (
                        <td
                          className={clsx(paddingClasses[size], "text-right")}
                        >
                          <RowActions
                            record={record}
                            index={index}
                            actions={actions}
                          />
                        </td>
                      )}
                    </tr>

                    {/* Expanded row */}
                    {expandable &&
                      isExpanded &&
                      expandable.expandedRowRender && (
                        <tr>
                          <td
                            colSpan={
                              columns.length +
                              (selection ? 1 : 0) +
                              (expandable ? 1 : 0) +
                              (actions.length > 0 ? 1 : 0)
                            }
                            className="px-0 py-0"
                          >
                            <div className="p-4 bg-gray-50 dark:bg-dark-700">
                              {expandable.expandedRowRender(record, index)}
                            </div>
                          </td>
                        </tr>
                      )}
                  </Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Footer */}
      {footerRender && (
        <div className="bg-gray-50 dark:bg-dark-800 border-t border-gray-200 dark:border-dark-700">
          {footerRender()}
        </div>
      )}

      {/* Pagination */}
      {pagination && <TablePagination {...pagination} />}
    </div>
  );

  // Wrap with responsive container if needed
  if (responsive) {
    return <div className="w-full overflow-auto">{tableContent}</div>;
  }

  return tableContent;
};

export default DataTable;
